import React from "react";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  showHome?: boolean;
}

export default function Breadcrumbs({
  items,
  showHome = true,
}: BreadcrumbsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  // Hide breadcrumbs on main/root pages
  const isRootPage =
    location.pathname === "/" ||
    location.pathname === "/episodes" ||
    location.pathname === "/projects" ||
    location.pathname === "/users" ||
    location.pathname === "/shared-episodes" ||
    location.pathname === "/episodes-shared-with-me";

  const breadcrumbItems: BreadcrumbItem[] = showHome
    ? [
        {
          label: "Home",
          path: "/",
          icon: <HomeIcon sx={{ fontSize: 18 }} />,
        },
        ...items,
      ]
    : items;

  // Hide breadcrumbs on mobile or root pages
  if (isMobile || isRootPage) {
    return null;
  }

  return (
    <Box
      sx={{
        py: 2,
        px: 3,
        backgroundColor: (themeMode) =>
          themeMode.palette.mode === "light"
            ? themeMode.palette.grey[50]
            : themeMode.palette.grey[900],
      }}
    >
      <MuiBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: "#6c757d",
          },
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          if (isLast || !item.path) {
            return (
              <Typography
                key={item.label}
                color={isLast ? "text.primary" : "text.secondary"}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "0.875rem",
                  fontWeight: isLast ? 600 : 400,
                }}
              >
                {item.icon}
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={item.label}
              component={RouterLink}
              to={item.path}
              color="inherit"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "0.875rem",
                color: "#1976d2",
                textDecoration: "none",
                "&:hover": {
                  color: "#1565c0",
                  textDecoration: "underline",
                },
              }}
            >
              {item.icon}
              {item.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
}
